import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Heart, CheckCircle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_a0c1db51-c8f4-4a5f-a45d-0782cce3dcb3/artifacts/a8j3q28b_IMG_7133.jpeg";

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Shield className="w-6 h-6" strokeWidth={1.5} />,
      title: "Practitioner-Quality",
      description: "Access the same professional-grade Standard Process and MediHerb supplements trusted by healthcare practitioners."
    },
    {
      icon: <Heart className="w-6 h-6" strokeWidth={1.5} />,
      title: "Whole-Food Based",
      description: "Made from real, whole-food organic ingredients for optimal absorption and bioavailability."
    },
    {
      icon: <CheckCircle className="w-6 h-6" strokeWidth={1.5} />,
      title: "Personalized Protocols",
      description: "Receive supplement recommendations tailored to your unique health needs and wellness goals."
    }
  ];

  const steps = [
    { num: "01", text: "Answer a brief wellness questionnaire" },
    { num: "02", text: "Receive your personalized supplement protocol" },
    { num: "03", text: "Add products directly to your cart" },
    { num: "04", text: "Begin your wellness journey" }
  ];

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="landing-page">
      {/* Announcement Bar */}
      <div className="announcement-bar text-center overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-8">
          <span>SUPER FAST SHIPPING, FREE OVER $99</span>
          <span className="hidden sm:inline">*</span>
          <span className="hidden sm:inline">OVER 500+ 5-STAR CUSTOMER REVIEWS</span>
          <span className="hidden md:inline">*</span>
          <span className="hidden md:inline">AUTHORIZED STANDARD PROCESS PARTNER</span>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-xl tracking-tight text-[#1a1a1a]">Fast Track To Health</span>
            <span className="text-xs text-gray-400 hidden sm:inline">|</span>
            <span className="text-sm text-gray-500 hidden sm:inline">Supplement Finder</span>
          </div>
          <Button
            onClick={() => navigate('/quiz')}
            className="btn-primary"
            data-testid="header-start-quiz-btn"
          >
            Start Quiz
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              {/* Rating */}
              <div className="flex items-center gap-2 mb-6">
                <span className="text-xs uppercase tracking-widest text-gray-500 font-medium">4.85 Rating</span>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-xs text-gray-500">from 500+ Reviews</span>
              </div>

              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1a1a1a] leading-[1.1] mb-6">
                Find the Right Supplements for Your Needs
              </h1>
              <p className="text-base text-gray-600 leading-relaxed mb-8 max-w-lg">
                Answer a few simple questions and receive a personalized supplement protocol 
                designed to support your unique wellness journey. Powered by Standard Process whole-food supplements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate('/quiz')}
                  className="btn-primary text-base px-10 py-4"
                  data-testid="hero-start-quiz-btn"
                >
                  Start the Quiz
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
                <a
                  href="https://fasttracktohealth.shop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-[#1a1a1a] hover:text-gray-600 text-sm font-medium transition-colors px-4 py-3"
                  data-testid="shop-link-btn"
                >
                  Shop All Supplements
                  <ArrowRight className="ml-1 w-4 h-4" strokeWidth={1.5} />
                </a>
              </div>
            </div>
            <div className="relative order-1 md:order-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={HERO_IMAGE}
                  alt="Fast Track To Health - Supplement recommendations"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 hidden md:block border border-[#d0e4d4]">
                <p className="text-xs text-gray-500 mb-1">Trusted by</p>
                <p className="text-2xl font-serif text-[#1a1a1a]">5,000+</p>
                <p className="text-sm text-gray-600">Health-conscious customers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3">
              Simple Process
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base">
              Get your personalized supplement recommendations in just a few minutes
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 stagger-children">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#d4e4bc] text-[#1a1a1a] flex items-center justify-center mx-auto mb-4 text-sm font-semibold tracking-wide">
                  {step.num}
                </div>
                <p className="text-gray-700 text-sm">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3">
              Why Choose Us
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] mb-4">
              Practitioner-Guided Wellness
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 stagger-children">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-8 border border-[#d0e4d4] card-hover"
              >
                <div className="w-14 h-14 rounded-full bg-[#d4e4bc] flex items-center justify-center mb-6 text-[#1a1a1a]">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-xl text-[#1a1a1a] mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3">
                Your Results
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] mb-6">
                What You'll Receive
              </h2>
              <div className="space-y-4">
                {[
                  "Personalized support area analysis",
                  "Recommended supplement protocol",
                  "Educational information about each product",
                  "Easy one-click add to cart"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#d4e4bc] flex items-center justify-center mt-0.5 flex-shrink-0">
                      <CheckCircle className="w-3.5 h-3.5 text-[#1a1a1a]" strokeWidth={2} />
                    </div>
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => navigate('/quiz')}
                className="btn-primary mt-8 px-8 py-3"
                data-testid="expect-start-quiz-btn"
              >
                Take the Quiz
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden bg-[#EFF3E1] flex items-center justify-center p-10">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-[#d4e4bc] flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-10 h-10 text-[#1a1a1a]" strokeWidth={1.5} />
                  </div>
                  <p className="font-serif text-2xl text-[#1a1a1a] mb-2">Standard Process</p>
                  <p className="text-sm text-gray-600">The gold standard in whole food<br/>organic vitamins and herbs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Ready to Start Your Wellness Journey?
          </h2>
          <p className="text-white/70 text-base mb-8 max-w-2xl mx-auto">
            Take our quick quiz and discover the supplements that may help support your health goals.
          </p>
          <Button
            onClick={() => navigate('/quiz')}
            className="bg-[#d4e4bc] text-[#1a1a1a] hover:bg-[#c4d4ac] px-10 py-4 text-base font-medium shadow-lg"
            style={{ borderRadius: '4px' }}
            data-testid="cta-start-quiz-btn"
          >
            Start the Quiz
            <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="font-serif text-lg text-[#1a1a1a]">Fast Track To Health</span>
            </div>
            <p className="text-sm text-gray-500 text-center">
              These recommendations are for educational purposes only and are not medical advice.
            </p>
            <a 
              href="/admin" 
              className="text-sm text-gray-400 hover:text-gray-600"
              data-testid="admin-link"
            >
              Admin
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
